import React, { useEffect, useState } from "react";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default function MemberDetail() {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showPassword, setShowPassword] = useState({});

  const navigate = useNavigate();

  // 🔹 Fetch members
  useEffect(() => {
    const fetchMembers = async () => {
      const snap = await getDocs(collection(db, "members"));
      setMembers(
        snap.docs.map(d => ({
          id: d.id,
          ...d.data(),
        }))
      );
    };
    fetchMembers();
  }, []);

  // 🔹 Active / Deactivate
  const toggleStatus = async (id, status) => {
    const newStatus = status === "ACTIVE" ? "DEACTIVE" : "ACTIVE";

    await updateDoc(doc(db, "members", id), {
      status: newStatus,
    });

    setMembers(prev =>
      prev.map(m =>
        m.id === id ? { ...m, status: newStatus } : m
      )
    );
  };

  // 🔹 Filter logic
  const filteredMembers = members.filter(m => {
    const searchMatch =
      m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.mobile?.includes(search) ||
      m.memberId?.includes(search);

    // Date handling (safe)
    let created = m.createdAt;
    if (created?.seconds) {
      created = new Date(created.seconds * 1000);
    } else {
      created = new Date(created);
    }

    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    const dateMatch =
      (!from || created >= from) &&
      (!to || created <= to);

    return searchMatch && dateMatch;
  });

  // 🔹 Excel
  const downloadExcel = () => {
    const data = filteredMembers.map((m, i) => ({
      Sr: i + 1,
      Date: m.createdAt,
      MemberID: m.memberId,
      Name: m.name,
      Mobile: m.mobile,
      Password: m.password,
      CreatedBy: m.createdBy,
      Voucher: m.voucherId,
      Status: m.status,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Members");
    XLSX.writeFile(wb, "Members.xlsx");
  };

  // 🔹 PDF
  const printPDF = () => {
    const pdf = new jsPDF();
    pdf.text("Member Details", 14, 10);

    pdf.autoTable({
      startY: 20,
      head: [[
        "Sr", "Date", "Member ID", "Name", "Mobile",
        "Password", "Created By", "Voucher", "Status"
      ]],
      body: filteredMembers.map((m, i) => [
        i + 1,
        m.createdAt,
        m.memberId,
        m.name,
        m.mobile,
        m.password,
        m.createdBy,
        m.voucherId,
        m.status,
      ]),
    });

    pdf.save("Members.pdf");
  };

  return (
    <div className="container-fluid">
      <h4 className="mb-3">Member Details</h4>

      {/* 🔍 Filters */}
      <div className="row mb-3">
        <div className="col-md-3">
          <input
            className="form-control"
            placeholder="Search Name / Mobile / ID"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="col-md-2">
          <input type="date" className="form-control" onChange={e => setFromDate(e.target.value)} />
        </div>

        <div className="col-md-2">
          <input type="date" className="form-control" onChange={e => setToDate(e.target.value)} />
        </div>

        <div className="col-md-2">
          <button className="btn btn-success w-100" onClick={downloadExcel}>
            ⬇ Excel
          </button>
        </div>

        <div className="col-md-2">
          <button className="btn btn-danger w-100" onClick={printPDF}>
            🖨 PDF
          </button>
        </div>
      </div>

      {/* 📋 Table */}
      <table className="table table-bordered table-hover shadow-sm">
        <thead className="table-primary text-center">
          <tr>
            <th>Sr</th>
            <th>Date</th>
            <th>Member ID</th>
            <th>Name</th>
            <th>Mobile</th>
            <th>Password</th>
            <th>Created By</th>
            <th>Voucher</th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          {filteredMembers.map((m, i) => (
            <tr key={m.id} className="text-center">
              <td>{i + 1}</td>
              <td>{m.createdAt}</td>

              {/* ✅ CLICKABLE MEMBER ID */}
              <td>
                <button
                  className="btn btn-link p-0 fw-bold"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/group-details/${m.memberId}`)}
                >
                  {m.memberId}
                </button>
              </td>

              <td>{m.name}</td>
              <td>{m.mobile}</td>

              {/* 👁 Password */}
              <td>
                {showPassword[m.id] ? m.password : "••••••"}
                <button
                  className="btn btn-sm btn-link"
                  onClick={() =>
                    setShowPassword(prev => ({
                      ...prev,
                      [m.id]: !prev[m.id],
                    }))
                  }
                >
                  👁️
                </button>
              </td>

              <td>{m.createdBy}</td>
              <td>{m.voucherId}</td>

              {/* 🟢 Status */}
              <td>
                <button
                  className={`btn btn-sm ${
                    m.status === "ACTIVE" ? "btn-success" : "btn-danger"
                  }`}
                  onClick={() => toggleStatus(m.id, m.status)}
                >
                  {m.status}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}